import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Switch
} from '@mui/material';
import { LocalHospital, AccessTime } from '@mui/icons-material';

// Reuse your SHIFT_PRESETS and UNAVAILABILITY_REASONS constants
const SHIFT_PRESETS = {
  'LD': { start: '08:00', end: '20:00', label: 'Long Day (8:00-20:00)' },
  'ND': { start: '20:00', end: '08:00', label: 'Night Duty (20:00-8:00)' },
  'AM': { start: '08:00', end: '16:00', label: 'Morning (8:00-16:00)' },
  'PM': { start: '14:00', end: '20:00', label: 'Afternoon (14:00-20:00)' },
  'TW': { start: '20:00', end: '00:00', label: 'Twilight (20:00-00:00)' },
  'ELD': { start: '07:30', end: '20:30', label: 'Extended Long Day (7:30-20:30)' },
  'ESCORT': { start: '08:00', end: '20:00', label: 'Escort (8:00-20:00)' },
};

const UNAVAILABILITY_REASONS = [
  'Sick Leave',
  'Maternity Leave',
  'Bereavement Leave',
  'Study Leave / Mandatory Training',
  'Force Majeure',
  'Paternity Leave',
  'Parent Leave',
  'Jury Leave',
  'Other'
];

const AvailabilityDialog = ({ 
  open, 
  onClose, 
  onSave, 
  dayData,
  isEditing = false 
}) => {
  const [formData, setFormData] = React.useState({
    startTime: '08:00',
    endTime: '16:00',
    isUnavailable: false,
    unavailabilityReason: '',
    unavailabilityType: 'Full Day',
    unavailabilityDetails: '',
    isSickLeave: false,
    sickLeaveType: 'Full Day',
    sicknessReason: '',
    comments: '',
    categoryOfStaff: '',
    wardDepartmentArea: ''
  });

  React.useEffect(() => {
    if (dayData) {
      setFormData({
        startTime: dayData.startTime || '08:00',
        endTime: dayData.endTime || '16:00',
        isUnavailable: dayData.isUnavailable || false,
        unavailabilityReason: dayData.unavailabilityReason || '',
        unavailabilityType: dayData.unavailabilityType || 'Full Day',
        unavailabilityDetails: dayData.unavailabilityDetails || '',
        isSickLeave: dayData.isSickLeave || false,
        sickLeaveType: dayData.sickLeaveType || 'Full Day',
        sicknessReason: dayData.sicknessReason || '',
        comments: dayData.comments || '',
        categoryOfStaff: dayData.categoryOfStaff || dayData.jobTitle || '',
        wardDepartmentArea: dayData.ward || ''
      });
    }
  }, [dayData]);

  const handleSubmit = () => {
    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Edit Availability' : 'Set Availability'}
        {dayData && (
          <Typography variant="subtitle2" color="textSecondary">
            {dayData.staffName} • {dayData.day} (Week {dayData.week})
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Time"
              type="time"
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Time"
              type="time"
              value={formData.endTime}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isUnavailable}
                  onChange={(e) => {
                    const isUnavailable = e.target.checked;
                    setFormData({
                      ...formData,
                      isUnavailable: isUnavailable,
                      unavailabilityReason: isUnavailable ? formData.unavailabilityReason : '',
                      unavailabilityType: isUnavailable ? 'Full Day' : 'Full Day'
                    });
                  }}
                  color="error"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 16, 
                    height: 16, 
                    bgcolor: 'error.light',
                    borderRadius: '2px',
                    border: '1px solid #d32f2f'
                  }} />
                  <Typography variant="body2" color={formData.isUnavailable ? 'error.main' : 'text.primary'}>
                    Mark as Unavailable
                  </Typography>
                  {formData.isUnavailable && (
                    <LocalHospital fontSize="small" color="error" />
                  )}
                </Box>
              }
            />
          </Grid>
          
          {formData.isUnavailable && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Unavailability Reason</InputLabel>
                  <Select
                    value={formData.unavailabilityReason}
                    label="Unavailability Reason"
                    onChange={(e) => setFormData({ ...formData, unavailabilityReason: e.target.value })}
                  >
                    <MenuItem value="">Select Reason...</MenuItem>
                    {UNAVAILABILITY_REASONS.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Unavailability Type</InputLabel>
                  <Select
                    value={formData.unavailabilityType}
                    label="Unavailability Type"
                    onChange={(e) => setFormData({ ...formData, unavailabilityType: e.target.value })}
                  >
                    <MenuItem value="Full Day">Full Day</MenuItem>
                    <MenuItem value="Half Day AM">Half Day (Morning)</MenuItem>
                    <MenuItem value="Half Day PM">Half Day (Afternoon)</MenuItem>
                    <MenuItem value="Partial Day">Partial Day</MenuItem>
                    <MenuItem value="Multi-Day">Multi-Day</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Unavailability Details (Optional)"
                  value={formData.unavailabilityDetails}
                  onChange={(e) => setFormData({ ...formData, unavailabilityDetails: e.target.value })}
                  multiline
                  rows={2}
                  placeholder="Additional details about the unavailability..."
                  size="small"
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Comments (Optional)"
              value={formData.comments}
              onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
              multiline
              rows={3}
              placeholder="Any additional notes..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvailabilityDialog;